import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle, MatDialogActions } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';
import {
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { MeetingAttendanceStatusLabels } from 'src/app/enums/meeting-attendance-status.enum';
import { TripTransportTypeLabels } from 'src/app/enums/trip-transport-type.enum';
import { getClockRegisterTypeLabel } from 'src/app/enums/clock-register-type.enum';
import { getDrivingRequestEvidenceRelationVisual } from 'src/app/enums/driving-request-evidence-relation.enum';
import { DrivingRequestLocationEvidenceKindEnum } from 'src/app/enums/driving-request-location-evidence-kind.enum';
import {
  getDrivingRequestVotingConsensusChipTone,
  getDrivingRequestVotingConsensusLabel,
} from 'src/app/enums/driving-request-voting-consensus.enum';
import {
  DecisionSummaryChip,
  DrivingRequestDriverCandidate,
  DrivingRequestEvidenceSummary,
  DrivingRequestResolutionContext,
  DrivingRequestResolutionScenario,
  DrivingRequestVotingSummary,
  UpdateDrivingRequestBody,
} from 'src/app/models/driving_request.model';
import { SecondsToStringTimePipe } from 'src/app/pipes/seconds-to-string-time.pipe';
import { ApiService } from 'src/app/services';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-driving-request-resolution-dialog',
  templateUrl: './driving-request-resolution-dialog.component.html',
  styleUrl: './driving-request-resolution-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DatePipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinner,
    MatRadioModule,
    MatExpansionModule,
    MatTooltipModule,
    SecondsToStringTimePipe,
  ],
})
export class DrivingRequestResolutionDialogComponent {
  readonly EmployeeRequestResponsibleForAnswerStatusEnum = EmployeeRequestResponsibleForAnswerStatusEnum;
  readonly EmployeeRequestResponsibleForAnswerStatusLabels = EmployeeRequestResponsibleForAnswerStatusLabels;
  readonly MeetingAttendanceStatusLabels = MeetingAttendanceStatusLabels;
  readonly TripTransportTypeLabels = TripTransportTypeLabels;
  readonly DrivingRequestLocationEvidenceKindEnum = DrivingRequestLocationEvidenceKindEnum;
  readonly getClockRegisterTypeLabel = getClockRegisterTypeLabel;
  readonly getEvidenceRelationVisual = getDrivingRequestEvidenceRelationVisual;

  readonly trip_driver_user_id = new FormControl<number | null>(null);
  readonly selected_driver_id = toSignal(this.trip_driver_user_id.valueChanges, { initialValue: this.trip_driver_user_id.value });

  readonly loading = signal(true);
  readonly load_error = signal<string | null>(null);
  readonly context = signal<DrivingRequestResolutionContext | null>(null);

  readonly submitting = signal(false);
  readonly submit_error = signal<string | null>(null);

  readonly data = inject<DrivingRequestResolutionDialogData>(MAT_DIALOG_DATA);

  private readonly api = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly dialog_ref = inject(MatDialogRef<DrivingRequestResolutionDialogComponent, boolean>);
  private readonly utils = inject(UtilsService);

  readonly formatDistanceMeters = (distance_in_meters: number | null): string => this.utils.formatDistanceMeters(distance_in_meters);
  readonly joinUserNames = (users: { full_name: string }[]): string => this.utils.joinUserNames(users);

  readonly candidates = computed<DrivingRequestDriverCandidate[]>(() => {
    const context = this.context();

    return context ? this.sortCandidatesByVotes(context.candidates) : [];
  });

  readonly scenario = computed<DrivingRequestResolutionScenario | null>(() => {
    const context = this.context();

    return context ? this.resolveScenario(context) : null;
  });

  readonly voting_summary_chip = computed<DecisionSummaryChip | null>(() => {
    const context = this.context();

    return context ? this.buildVotingSummaryChip(context.voting_summary) : null;
  });

  readonly evidence_summary_chips = computed<DecisionSummaryChip[]>(() => {
    const context = this.context();

    return context ? this.buildEvidenceSummaryChips(context.evidence_summary) : [];
  });

  readonly can_be_resolved = computed<boolean>(() => this.context()?.request.can_be_resolved ?? false);

  readonly requires_driver_selection = computed<boolean>(() => this.candidates().length > 0);

  readonly show_driver_selection_hint = computed<boolean>(
    () => this.requires_driver_selection() && this.selected_driver_id() === null && this.can_be_resolved()
  );

  readonly can_accept = computed<boolean>(
    () => this.can_be_resolved() && !this.submitting() && (!this.requires_driver_selection() || this.selected_driver_id() !== null)
  );

  readonly actions_disabled = computed<boolean>(() => this.loading() || this.submitting() || !this.can_be_resolved());

  readonly selected_candidate = computed<DrivingRequestDriverCandidate | null>(
    () => this.candidates().find(candidate => candidate.user_id === this.selected_driver_id()) ?? null
  );

  constructor() {
    this.loadContext();
  }

  retryLoad(): void {
    this.loadContext();
  }

  resolve(status: EmployeeRequestResponsibleForAnswerStatusEnum): void {
    if (this.actions_disabled()) {
      return;
    }

    const accepts_request = status === EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED;

    if (accepts_request && !this.can_accept()) {
      return;
    }

    const selected_driver_id = this.selected_driver_id();

    const body: UpdateDrivingRequestBody = {
      status,
      ...(accepts_request && selected_driver_id !== null ? { trip_driver_user_id: selected_driver_id } : {}),
    };

    this.submit_error.set(null);
    this.submitting.set(true);

    this.api
      .updateDrivingRequest(this.data.driving_request_id, body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => this.dialog_ref.close(true),
        error: (error: HttpErrorResponse): void => {
          this.submit_error.set(this.extractErrorMessage(error));
          this.submitting.set(false);
        },
      });
  }

  private loadContext(): void {
    this.loading.set(true);
    this.load_error.set(null);

    this.api
      .getDrivingRequestResolutionContext(this.data.driving_request_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (context: DrivingRequestResolutionContext): void => {
          this.context.set(context);
          this.preselectResolvedDriver(context);
          this.loading.set(false);
        },
        error: (error: HttpErrorResponse): void => {
          this.load_error.set(this.extractErrorMessage(error));
          this.loading.set(false);
        },
      });
  }

  private preselectResolvedDriver(context: DrivingRequestResolutionContext): void {
    const resolved_driver_id = context.request.resolved_driver?.id ?? null;
    const is_still_candidate =
      resolved_driver_id !== null && context.candidates.some(candidate => candidate.user_id === resolved_driver_id);

    if (is_still_candidate) {
      this.trip_driver_user_id.setValue(resolved_driver_id);
    }
  }

  /**
   * Escenario derivado de los datos del contexto:
   * - no_candidates: sin candidatos a conductor
   * - individual: sin votos útiles de otros asistentes (total_answering_users <= 1)
   * - voting: más de un asistente pudo señalar conductor
   */
  private resolveScenario(context: DrivingRequestResolutionContext): DrivingRequestResolutionScenario {
    if (context.candidates.length === 0) {
      return 'no_candidates';
    }

    const voting_summary = context.voting_summary;

    if (voting_summary === null || voting_summary.total_answering_users <= 1) {
      return 'individual';
    }

    return 'voting';
  }

  private sortCandidatesByVotes(candidates: DrivingRequestDriverCandidate[]): DrivingRequestDriverCandidate[] {
    return [...candidates].sort((a: DrivingRequestDriverCandidate, b: DrivingRequestDriverCandidate): number => {
      if (b.votes_count !== a.votes_count) {
        return b.votes_count - a.votes_count;
      }

      return a.full_name.localeCompare(b.full_name, 'es');
    });
  }

  private buildVotingSummaryChip(voting_summary: DrivingRequestVotingSummary | null): DecisionSummaryChip | null {
    if (voting_summary === null) {
      return null;
    }

    return {
      label: getDrivingRequestVotingConsensusLabel(voting_summary.consensus),
      tone: getDrivingRequestVotingConsensusChipTone(voting_summary.consensus),
    };
  }

  private buildEvidenceSummaryChips(evidence_summary: DrivingRequestEvidenceSummary): DecisionSummaryChip[] {
    const chips: DecisionSummaryChip[] = [];

    if (evidence_summary.is_insufficient) {
      chips.push({ label: 'Evidencias insuficientes', tone: 'warning' });
    } else {
      const plural = evidence_summary.total_count === 1 ? '' : 's';
      chips.push({ label: `${evidence_summary.total_count} evidencia${plural} encontrada${plural}`, tone: 'neutral' });
    }

    if (evidence_summary.compatible_count > 0) {
      const plural = evidence_summary.compatible_count === 1 ? '' : 's';
      chips.push({ label: `${evidence_summary.compatible_count} compatible${plural}`, tone: 'positive' });
    }

    if (evidence_summary.has_contradictions) {
      const plural = evidence_summary.contradictory_count === 1 ? '' : 's';
      chips.push({ label: `${evidence_summary.contradictory_count} contradictoria${plural}`, tone: 'danger' });
    }

    return chips;
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const backend_message = error.error?.error ?? error.error?.message;

    if (typeof backend_message === 'string' && backend_message.trim() !== '') {
      return backend_message;
    }

    return 'Ha ocurrido un error inesperado. Inténtalo de nuevo.';
  }
}

export interface DrivingRequestResolutionDialogData {
  driving_request_id: number;
}
